/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useCallback, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'

// Chakra Components
import {
    Box,
    Button,
    Link,
    Flex,
    AspectRatio,
    Skeleton as ChakraSkeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import Badges from '../product-badges'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getVideoAssets, buildMediaSequence} from '../../hooks/use-product-media'

// Lazy load ProductZoomModal and ProductVideoPlayer- only loaded when user clicks to zoom an image
// This defers Chakra Modal components until needed
const ProductZoomModal = loadable(() => import('../product-zoom-modal'), {ssr: false})
const ProductVideoPlayer = loadable(() => import('../product-video-player'), {ssr: false})

// Constants
const VIEW_TYPE_LARGE = 'large'
const VIEW_TYPE_GIFT_CARD = 'giftCard'
const VIEW_TYPE_ZOOM = 'zoom'
const SKELETON_ITEMS_COUNT = 7
const MOBILE_GAP = 4 // Gap between images in mobile carousel (px)
const DESKTOP_GAP = 8 // Gap between images in desktop grid (px)
export const DYNAMIC_IMAGE_MAX_WIDTH = '1280px'

// Image widths config for different grid positions
// The gallery takes ~50% of viewport, then images are 50% or 33% of that
// Desktop large: ~30vw, Desktop small: ~20vw
// Mobile: full width carousel
const IMAGE_WIDTHS = {
    // First 4 images (~30% of viewport on desktop)
    large: {base: '100vw', md: '50vw', lg: '30vw'},
    // Remaining images (~20% of viewport on desktop)
    small: {base: '100vw', md: '50vw', lg: '20vw'},
    // Mobile carousel (full width)
    mobile: {base: '100vw', md: '100vw', lg: '100vw'}
}

// Custom magnifying glass cursor using SVG data URL
const MAGNIFYING_GLASS_CURSOR = `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='rgba(255,255,255,0.85)' d='M0 0h28v28H0z'/%3E%3Cg clip-path='url(%23a)'%3E%3Cpath d='M12.33 9.833v5m-2.5-2.5h5m7.09 9.59-4.59-4.59m-5 1.667a6.667 6.667 0 1 0 0-13.334 6.667 6.667 0 0 0 0 13.334Z' stroke='%2325252B' stroke-width='1.1' stroke-linecap='square'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M4 4h20v20H4z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E") 12 12, zoom-in`

// ── Internal hook — not exported, only used by VideoSlot below
const useInViewport = (rootMargin = '200px') => {
    const ref = useRef(null)
    const [hasEntered, setHasEntered] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el || hasEntered) return

        if (typeof IntersectionObserver === 'undefined') {
            setHasEntered(true)
            return
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setHasEntered(true)
                    observer.disconnect()
                }
            },
            {rootMargin}
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [hasEntered, rootMargin])

    return [ref, hasEntered]
}

const VideoSlot = ({item, getImageUrl, loadingStrategy}) => {
    const [ref, hasEntered] = useInViewport('200px')

    return (
        <Box ref={ref} position="relative">
            {hasEntered ? (
                <VideoItem
                    asset={item.asset}
                    fallbackImage={item.fallbackImage}
                    getImageUrl={getImageUrl}
                    loadingStrategy={loadingStrategy}
                />
            ) : (
                <AspectRatio ratio={2 / 3}>
                    <Box bg="gray.100" />
                </AspectRatio>
            )}
        </Box>
    )
}

// PDP-specific sequence parser — returns ALL valid positions
const parseSequencePositionsPDP = (raw) => {
    if (!raw) return []
    return String(raw)
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n >= 1)
}

/**
 * Skeleton loading component for image gallery
 * Displays placeholder grid while images are loading
 */
export const Skeleton = () => {
    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Box
                display="grid"
                gridTemplateColumns="repeat(6, 1fr)"
                gap={`${DESKTOP_GAP}px`}
            >
                {Array.from({length: SKELETON_ITEMS_COUNT}, (_, index) => (
                    <Box key={index} gridColumn={index < 4 ? 'span 3' : 'span 2'}>
                        <AspectRatio ratio={2 / 3}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.string
}

/**
 * Individual image component with click handling and loading optimization
 * @param {boolean} isAboveFold - Whether image is above the fold (LCP candidate)
 * @param {Object} widths - Responsive widths config for DynamicImage
 */
const ImageItem = ({
    image,
    index,
    onImageClick,
    cursor,
    alt,
    loadingStrategy,
    getImageUrl,
    isAboveFold = false,
    widths = IMAGE_WIDTHS.large,
    imageProps = {},
    imageContainerProps = {}
}) => {
    return (
        <AspectRatio ratio={2 / 3} {...imageContainerProps}>
            <DynamicImage
                src={getImageUrl(image)}
                widths={widths}
                imageProps={{
                    alt: alt || image.alt,
                    loading: loadingStrategy,
                    decoding: isAboveFold ? 'sync' : 'async',
                    fetchpriority: isAboveFold ? 'high' : 'auto',
                    onClick: () => onImageClick?.(index),
                    style: {cursor, objectFit: 'cover'},
                    ...imageProps
                }}
            />
        </AspectRatio>
    )
}

ImageItem.propTypes = {
    image: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    onImageClick: PropTypes.func,
    cursor: PropTypes.string,
    alt: PropTypes.string,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired,
    getImageUrl: PropTypes.func.isRequired,
    isAboveFold: PropTypes.bool,
    widths: PropTypes.object,
    imageProps: PropTypes.object,
    imageContainerProps: PropTypes.object
}

/**
 * Wraps ProductVideoPlayer in the same AspectRatio shell as ImageItem so it
 * occupies exactly the same grid cell. Videos on PDP are always isActive=true
 * — playback is driven solely by the IntersectionObserver inside the player.
 */
const VideoItem = ({asset, fallbackImage, getImageUrl, loadingStrategy}) => {
    const styles = useMultiStyleConfig('ImageGalleryVideo', {})
    const src = asset?.disBaseLink || asset?.link
    const [hasError, setHasError] = useState(false)
    const [isReady, setIsReady] = useState(false)

    if (!src || hasError) {
        if (!fallbackImage) return null
        return (
            <AspectRatio ratio={2 / 3}>
                <DynamicImage
                    src={getImageUrl(fallbackImage)}
                    widths={IMAGE_WIDTHS.large}
                    imageProps={{
                        alt: fallbackImage.alt || '',
                        loading: loadingStrategy,
                        objectFit: 'cover'
                    }}
                />
            </AspectRatio>
        )
    }

    return (
        <AspectRatio ratio={2 / 3}>
            <Box {...styles.container} data-testid="sf-image-gallery-video-container">
                {/* Video player — always mounted so HLS can initialise */}
                <Box {...styles.videoWrapper} opacity={isReady ? 1 : 0} data-testid="sf-image-gallery-video-wrapper">
                    <ProductVideoPlayer
                        src={src}
                        isActive={true}
                        onReady={() => setIsReady(true)}
                        onError={() => setHasError(true)}
                    />
                </Box>

                {/* Spinner sits above the video with a blurred backdrop */}
                {!isReady && (
                    <Box {...styles.spinnerWrapper} data-testid="sf-image-gallery-video-loading">
                        <Box {...styles.spinnerBackdrop} />
                        <Box
                            as="span"
                            {...styles.spinner}
                            data-testid="sf-image-gallery-spinner"
                            sx={{
                                '@keyframes igVideoSpin': {
                                    from: {transform: 'rotate(0deg)'},
                                    to: {transform: 'rotate(360deg)'}
                                },
                                animation: 'igVideoSpin 0.8s linear infinite'
                            }}
                        />
                    </Box>
                )}
            </Box>
        </AspectRatio>
    )
}

VideoItem.propTypes = {
    asset: PropTypes.shape({
        link: PropTypes.string,
        disBaseLink: PropTypes.string
    }).isRequired,
    fallbackImage: PropTypes.object,
    getImageUrl: PropTypes.func.isRequired,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired
}

/**
 * Responsive gallery component - single DOM structure
 * Mobile (base to lg): horizontal scroll carousel with custom scrollbar
 * Desktop (lg+): CSS grid with 6 columns (first 4 images span 3, rest span 2)
 */
const ResponsiveGallery = ({
    mediaItems = [],
    badges,
    onImageClick,
    onSlideChange,
    getImageUrl,
    loadingStrategy,
    imageContainerProps,
    imageProps
}) => {
    const scrollContainerRef = useRef(null)
    const [scrollProgress, setScrollProgress] = useState(0)
    const [activeIndex, setActiveIndex] = useState(0)

    // Track how many images appear before each slot so the zoom modal index
    // stays correct even when video items are interspersed.
    const imageIndexMap = useMemo(() => {
        let count = 0
        return mediaItems.map((item) => {
            if (item.type === 'image') return count++
            return null
        })
    }, [mediaItems])

    // Update scroll progress and active index (mobile only)
    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const {scrollLeft, scrollWidth, clientWidth} = container
        const maxScroll = scrollWidth - clientWidth

        // Calculate scroll progress (0 to 1)
        const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0
        setScrollProgress(progress)

        // Calculate active slide index
        const slideWidth = clientWidth + MOBILE_GAP
        const newActiveIndex = Math.round(scrollLeft / slideWidth)

        if (newActiveIndex !== activeIndex && newActiveIndex >= 0 && newActiveIndex < mediaItems.length) {
            setActiveIndex(newActiveIndex)
            onSlideChange?.({activeIndex: newActiveIndex})
        }
    }, [activeIndex, mediaItems.length, onSlideChange])

    // Set up scroll listener for mobile carousel
    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) return

        updateScrollState()
        container.addEventListener('scroll', updateScrollState, {passive: true})
        return () => container.removeEventListener('scroll', updateScrollState)
    }, [updateScrollState])

    // Calculate scrollbar thumb width for mobile
    const scrollbarThumbWidth = mediaItems.length > 0 ? 100 / mediaItems.length : 100

    return (
        <Box position="relative">
            {/* Badges - positioned outside grid/carousel to not affect nth-of-type counting */}
            {/* Mobile: offset by -16px margin + 8px padding = -8px from edge */}
            <Box position="absolute" top={2} left={{base: '-8px', lg: 0}} zIndex={1}>
                <Badges badges={badges} />
            </Box>

            {/* Single container that switches between carousel (mobile) and grid (desktop) */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    // Mobile: horizontal scroll carousel
                    display: 'flex',
                    gap: `${MOBILE_GAP}px`,
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {display: 'none'},
                    // Mobile: each item is full width
                    '& > *': {
                        flexShrink: 0,
                        width: '100vw'
                    },
                    // Desktop (lg+): switch to CSS grid
                    '@media (min-width: 62em)': {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        gap: `${DESKTOP_GAP}px`,
                        overflowX: 'visible',
                        // Desktop: items use grid column spans
                        '& > *': {
                            width: 'auto'
                        },
                        // First 4 images span 3 columns each
                        '& > *:nth-of-type(-n+4)': {
                            gridColumn: 'span 3'
                        },
                        // Remaining images span 2 columns each
                        '& > *:nth-of-type(n+5)': {
                            gridColumn: 'span 2'
                        }
                    }
                }}
                mx={{base: '-16px', lg: 0}}
            >
                {mediaItems?.map((item, index) => {
                    if (item?.type === 'video') {
                        return (
                            <VideoSlot
                                key={`gallery-video-${index}`}
                                item={item}
                                getImageUrl={getImageUrl}
                                loadingStrategy={loadingStrategy}
                            />
                        )
                    }

                    const imageIndex = imageIndexMap[index]
                    const isAboveFold = imageIndex < 4
                    return (
                        <Box key={`gallery-${index}`} position="relative">
                            <Button
                                as={Link}
                                variant="unstyled"
                                href="#"
                                cursor={MAGNIFYING_GLASS_CURSOR}
                                onClick={(e) => {
                                    e.preventDefault()
                                    onImageClick(imageIndex)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        onImageClick(imageIndex)
                                    }
                                }}
                                aria-label={`View larger image ${imageIndex + 1} of ${
                                    mediaItems.length
                                }`}
                                display="block"
                                width="100%"
                                height="unset"
                            >
                                <ImageItem
                                    image={item?.asset}
                                    index={imageIndex}
                                    loadingStrategy={index < 4 ? 'eager' : loadingStrategy}
                                    getImageUrl={getImageUrl}
                                    isAboveFold={index < 4}
                                    widths={index < 4 ? IMAGE_WIDTHS.large : IMAGE_WIDTHS.small}
                                    imageProps={{
                                        width: '100%',
                                        ...imageProps
                                    }}
                                    imageContainerProps={{...imageContainerProps}}
                                />
                            </Button>
                        </Box>
                    )
                })}
            </Box>

            {/* Custom scrollbar for mobile - hidden on desktop */}
            {mediaItems?.length > 1 && (
                <Box
                    display={{base: 'block', lg: 'none'}}
                    position="relative"
                    height="2px"
                    backgroundColor="transparent"
                    mx="-16px"
                >
                    <Box
                        position="absolute"
                        top={0}
                        left={`${scrollProgress * (100 - scrollbarThumbWidth)}%`}
                        width={`${scrollbarThumbWidth}%`}
                        height="2px"
                        backgroundColor="#000"
                        borderRadius="0"
                    />
                </Box>
            )}
        </Box>
    )
}

ResponsiveGallery.propTypes = {
    mediaItems: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.oneOf(['image', 'video']).isRequired,
            asset: PropTypes.object.isRequired
        })
    ).isRequired,
    badges: PropTypes.array,
    onImageClick: PropTypes.func.isRequired,
    onSlideChange: PropTypes.func.isRequired,
    getImageUrl: PropTypes.func.isRequired,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired,
    imageContainerProps: PropTypes.object,
    imageProps: PropTypes.object
}

/**
 * Gift gallery component - desktop shows single image, mobile shows carousel
 * Uses CSS-based responsive switching for zero CLS
 */
const GiftGallery = ({images, badges, getImageUrl, loadingStrategy}) => {
    const mediaItems = useMemo(() => images?.map((asset) => ({type: 'image', asset})), [images])
    if (!images.length) return null

    return (
        <>
            {/* Desktop: single centered image */}
            <Flex
                display={{base: 'none', lg: 'flex'}}
                justifyContent="center"
                position="relative"
                width="100%"
            >
                <Badges badges={badges} />
                <Box width="100%" maxWidth="600px">
                    <ImageItem
                        image={mediaItems[0]?.asset}
                        index={0}
                        loadingStrategy={loadingStrategy}
                        getImageUrl={getImageUrl}
                        imageContainerProps={{
                            ratio: { lg: 1 }
                        }}
                        imageProps={{
                            borderRadius: {lg: '16px'},
                            width: { lg: '100%' }
                        }}
                    />
                </Box>
            </Flex>
            {/* Mobile: carousel */}
            <Box display={{base: 'block', lg: 'none'}}>
                <ResponsiveGallery
                    mediaItems={mediaItems}
                    badges={badges}
                    onImageClick={() => {}}
                    onSlideChange={() => {}}
                    getImageUrl={getImageUrl}
                    loadingStrategy={loadingStrategy}
                    imageProps={{
                        borderRadius: {base: '10px'}
                    }}
                    imageContainerProps={{
                        mx: {base: '64px'}
                    }}
                />
            </Box>
        </>
    )
}

GiftGallery.propTypes = {
    images: PropTypes.array.isRequired,
    badges: PropTypes.array,
    getImageUrl: PropTypes.func.isRequired,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired
}

/**
 * Main ImageGallery component
 * Renders responsive image gallery with zoom modal functionality
 * Single DOM structure that uses CSS to switch between:
 * - Mobile: horizontal scroll carousel with custom scrollbar
 * - Desktop: CSS grid layout
 */
const ImageGallery = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    size,
    lazy = false,
    badges = [],
    isGiftPage = false,
    videoSequencePositions = ''
}) => {
    // Hooks
    const styles = useMultiStyleConfig('ImageGallery', {size})

    // State
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)

    // Memoized values
    const heroImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: isGiftPage ? VIEW_TYPE_GIFT_CARD : VIEW_TYPE_LARGE,
                selectedVariationAttributes
            }),
        [imageGroups, selectedVariationAttributes, isGiftPage]
    )

    const zoomImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: VIEW_TYPE_ZOOM,
                selectedVariationAttributes
            }),
        [imageGroups, selectedVariationAttributes]
    )

    const heroImages = useMemo(() => heroImageGroup?.images || [], [heroImageGroup])
    const zoomImages = useMemo(
        () => zoomImageGroup?.images || heroImages,
        [zoomImageGroup, heroImages]
    )

    const loadingStrategy = useMemo(() => (lazy ? 'lazy' : 'eager'), [lazy])
    const {app: appConfig} = getConfig()
    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED =
        appConfig?.PRODUCT_IMGS_RESIZE_PARAMS_ENABLED == 'true'
    const enableProductVideo =
        appConfig?.GAR_ENABLE_PRODUCT_VIDEO === true ||
        appConfig?.GAR_ENABLE_PRODUCT_VIDEO === 'true'

    // ── Video assets
    const colorValue = selectedVariationAttributes?.color

    const videoAssets = useMemo(
        () => (enableProductVideo ? getVideoAssets(imageGroups, colorValue) : []),
        [imageGroups, colorValue, enableProductVideo]
    )

    const sequencePositions = useMemo(
        () => (enableProductVideo ? parseSequencePositionsPDP(videoSequencePositions) : []),
        [videoSequencePositions, enableProductVideo]
    )

    /**
     * Final merged list: [{type:'image'|'video', asset:{...}}]
     * Recomputes when images, videos, color variant, or positions change.
     */
    const mediaItems = useMemo(() => {
        const sequence = buildMediaSequence(heroImages, videoAssets, sequencePositions)

        let vidIdx = 0
        return sequence.map((item) => {
            if (item.type !== 'video') return item

            const pos = sequencePositions[vidIdx]
            const fallbackImage = heroImages[pos - 1] || heroImages[0] || null // image at that position

            return {
                ...item,
                fallbackImage,
                vidIdx: vidIdx++
            }
        })
    }, [heroImages, videoAssets, sequencePositions])

    // Pure image list for zoom modal (videos excluded)
    const imageOnlyItems = useMemo(
        () => mediaItems.filter((item) => item.type === 'image').map((item) => item.asset),
        [mediaItems]
    )

    // Callback functions
    const getImageUrl = useCallback(
        (image) => {
            const baseUrl = image.disBaseLink || image.link
            return PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
                ? `${baseUrl}[?sw={width}&q=75&fmt=webp]`
                : baseUrl
        },
        [PRODUCT_IMGS_RESIZE_PARAMS_ENABLED]
    )

    const handleImageClick = useCallback((imageIndex) => {
        setSelectedImageIndex(imageIndex)
        setIsModalOpen(true)
    }, [])

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false)
    }, [])

    const handleSlideChange = useCallback((data) => {
        setSelectedImageIndex(data.activeIndex)
    }, [])

    if (isGiftPage) {
        return (
            <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                h={{base: 'auto', lg: '100%'}}
                minH={{base: 'auto', lg: 'calc(100vh - 80px)'}}
                bg="gray.200"
            >
                <Box {...styles.heroImageGroup} alignContent="center" w="100%">
                    <GiftGallery
                        images={heroImages}
                        badges={badges}
                        getImageUrl={getImageUrl}
                        loadingStrategy={loadingStrategy}
                    />
                </Box>
            </Flex>
        )
    }

    return (
        <Flex direction="column">
            <Box {...styles.heroImageGroup}>
                <ResponsiveGallery
                    mediaItems={mediaItems}
                    badges={badges}
                    onImageClick={handleImageClick}
                    onSlideChange={handleSlideChange}
                    getImageUrl={getImageUrl}
                    loadingStrategy={loadingStrategy}
                />
            </Box>

            {/* Zoom modal for enlarged image viewing */}
            <ProductZoomModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                images={zoomImages}
                thumbImages={imageOnlyItems}
                initialImageIndex={selectedImageIndex}
                getImageUrl={getImageUrl}
            />
        </Flex>
    )
}

ImageGallery.propTypes = {
    imageGroups: PropTypes.array,
    selectedVariationAttributes: PropTypes.object,
    size: PropTypes.string,
    lazy: PropTypes.bool,
    badges: PropTypes.array,
    isGiftPage: PropTypes.bool,
    videoSequencePositions: PropTypes.string
}

export default ImageGallery
